import { DataTableStackCell } from '@/components/data-table/DataTable.component';
import FileUploadTable from '@/components/file-upload-table/FileUploadTable.component';
import { CONVERSION_TYPES, DOCUMENT_TYPES } from '@/consts/uploads';
import { PlanV2Dto } from '@/models';
import { Conversion, ConversionStatus } from '@/models/ConversionDTO.model';
import { ProgramFund } from '@/models/ops/investments/Program.model';
import { FeatureLevelPermissions } from '@/models/UserPermissions.model';
import ActionTableForm from '@/routes/plans/plan-detail/PlanActionTableV2/ConversionMainComponents/ActionTableForm.component';
import { ProgramService } from '@/services/ops/investments/Program.service';
import { PlanService } from '@/services/Plan.service';
import { SecurityMasterService } from '@/services/SecurityMaster.service';
import SecurityService from '@/services/suba/security/Security.service';
import { userService } from '@/services/User.service';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';

import { debounce, isString } from 'lodash';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useToggle } from 'react-use';
import * as yup from 'yup';

import ConversionCardButton from './ConversionMainComponents/ConversionCardButton.component';
import ConversionDialogHeader from './ConversionMainComponents/ConversionDialogHeader.component';
import { useConversionAgGrid } from './hooks';
import { useConversionFile } from './hooks/useEditFile';
import useUploadOnboardingAgGridFile from './hooks/useUploadOnboardingAgGridFile.hook';
import { FundOption } from './InvestmentElectionMapping/InvestmentElectionMappingFundAutocomplete.component';

type CusipMappingProps = {
  sponsorPlanId: number;
  conversion?: Conversion;
  fundMappings?: { oldCusip: string; newCusip: string }[];
  isHasActiveConversion?: boolean;
};

const notFoundSecurityError = 'Value not found in security table';
const securityCache = new Map<string, string>();

function fetchWithTimeout<T>(
  promise: Promise<T>,
  ms: number
): Promise<T | undefined> {
  return new Promise(resolve => {
    const timer = setTimeout(() => resolve(undefined), ms);
    promise
      .then(res => {
        clearTimeout(timer);
        resolve(res);
      })
      .catch(() => {
        clearTimeout(timer);
        resolve(undefined);
      });
  });
}

const validationSchema = (funds: string[]) =>
  yup.array().of(
    yup.object().when({
      is: row => {
        return !row.pristine;
      },
      otherwise: schema => schema,
      then: schema =>
        schema
          .shape({
            newFund: yup
              .string()
              .test(
                'funds-not-empty',
                'No funds available for selection',
                () => funds.length > 0
              )
              .oneOf(funds, 'Invalid fund selection')
              .required('New Fund is required'),
            oldFund: yup
              .string()
              .required('Old Fund is required')
              .test(
                'cusip-validation',
                'Invalid CUSIP/Ticker',
                async function (value) {
                  const { path, createError } = this;

                  if (!value)
                    return createError({
                      message: 'Old Fund is required',
                      path
                    });

                  if (securityCache.has(value)) {
                    const cachedCusip = securityCache.get(value)!;
                    if (cachedCusip) {
                      return true;
                    }
                  } else {
                    let fetchedCusip: string | undefined;
                    try {
                      fetchedCusip = await fetchWithTimeout(
                        SecurityService.get(value).then(r => r?.cusip),
                        10000
                      );
                    } catch {
                      fetchedCusip = undefined;
                    }
                    securityCache.set(value, fetchedCusip);

                    if (fetchedCusip) {
                      return true;
                    }
                  }

                  if (!/^\d{9}$/.test(value)) {
                    return createError({
                      message: 'Must be exactly 9 digits',
                      path
                    });
                  }

                  return createError({ message: notFoundSecurityError, path });
                }
              )
          })
          .test({
            test: function (object) {
              const errorsList = [['oldFund', 'Each Old Fund must be unique']]
                .map(([field, message]) => {
                  if (
                    this.parent
                      .filter((o: any) => o !== object)
                      .some((o: any) => {
                        return o?.oldFund && o?.oldFund === object?.oldFund;
                      })
                  ) {
                    return this.createError({
                      message,
                      path: `${this.path}.${field}`
                    });
                  }

                  return true;
                })
                .filter(error => error instanceof yup.ValidationError);

              if (errorsList.length > 0) {
                return new yup.ValidationError(
                  errorsList as yup.ValidationError[],
                  object,
                  this.path
                );
              }

              return true;
            }
          })
    })
  );

export const CusipMapping: React.FC<CusipMappingProps> = ({
  sponsorPlanId,
  conversion,
  isHasActiveConversion
}) => {
  const [fundMappings, setFundMappings] = useState<Record<string, FundOption>>(
    {}
  );
  const [planActionDialogOpened, togglePlanActionDialog] = useToggle(false);
  const conversionAgGrid = useConversionAgGrid(sponsorPlanId);
  const userFileNameRef = useRef('');
  const userInfo = userService.getUser();
  const isUploadingUser = userInfo?.nickname === conversion?.submittedBy?.id;
  const isNewConversion = !conversion;
  const isDraftStage =
    conversion?.status === ConversionStatus.Draft || !conversion?.status;
  const isViewMode =
    conversion?.status && conversion.status !== ConversionStatus.Draft;

  const debouncedSetField = useRef(
    debounce((field: string, value: any) => {
      conversionAgGrid.form.current?.setFieldValue(field, value);
    }, 800)
  ).current;

  //Clean up on unmount to avoid memory leaks
  useEffect(() => {
    return () => {
      debouncedSetField.cancel();
    };
  }, [debouncedSetField]);

  const handleCellChange = useCallback(
    (key: string, value) => {
      const matched = key.match(/[^[]+(?=])/);
      if (key.toLowerCase().endsWith('state') && matched && +matched[0] > -1) {
        const valueToSave = isString(value)
          ? value.toUpperCase().slice(0, 2)
          : value;

        conversionAgGrid?.form.current?.setFieldValue(key, valueToSave);

        return;
      }

      if (key.includes('pristine')) {
        conversionAgGrid?.form.current?.setFieldValue(key, value);
        return;
      }

      if (key.includes('oldFund')) {
        debouncedSetField(key, value);
        return;
      }

      if (key.match(/\[\d*].[a-zA-Z]*/)) {
        conversionAgGrid?.form.current?.setFieldValue(key, value);
      }
    },
    [conversionAgGrid?.form]
  );

  const { mutateAsync: uploadMappning, isLoading } =
    useUploadOnboardingAgGridFile(
      sponsorPlanId as number,
      DOCUMENT_TYPES.CUSIP_MAPPINGS,
      userFileNameRef.current
    );

  const sponsorPlanQuery = useQuery<PlanV2Dto>(
    ['PlanService.getPlanById', sponsorPlanId],
    () => PlanService.getPlanById(sponsorPlanId),
    {
      staleTime: Infinity
    }
  );

  const programId = sponsorPlanQuery.data?.data.relationships.program?.data.id;

  const programFundsQuery = useQuery<ProgramFund[]>(
    ['ProgramService.getProgramFunds', programId],
    () => ProgramService.getProgramFunds(programId),
    {
      enabled: !!programId,
      staleTime: Infinity
    }
  );

  const options = useMemo(
    () => programFundsQuery.data || [],
    [programFundsQuery]
  );

  const onConversionUpload = useCallback(
    async (data: Record<string, unknown>[]) => {
      conversionAgGrid.handleLoad(data);
      if (conversion?.investmentElectionMappings?.fundMappings) {
        const securitiesSearchLook =
          await SecurityMasterService.getSecuritiesByCusips(
            conversion.investmentElectionMappings.fundMappings.map(
              e => e.newCusip
            )
          );

        setFundMappings(
          conversion.investmentElectionMappings.fundMappings.reduce(
            (acc, curr) => ({
              ...acc,
              [curr.oldCusip]: securitiesSearchLook.find(
                e => e.cusip === curr.newCusip
              )
            }),
            {}
          )
        );
      }
    },
    [conversionAgGrid.handleLoad]
  );

  const columnDefs = useMemo(
    () => [
      {
        alternates: ['Old Fund'],
        editable: conversion?.status !== ConversionStatus.Complete,
        field: 'oldFund',
        headerName: 'Old Fund',
        required: true,
        type: 'text',
        valueParser: value =>
          typeof value === 'string' ? value.trim() : value,
        width: 200
      },
      {
        alternates: ['New Fund'],
        editable: conversion?.status !== ConversionStatus.Complete,
        field: 'newFund',
        getOptionLabel: option => {
          return typeof option === 'string'
            ? option
            : `${option?.cusip} | ${option?.symbol}`;
        },
        headerName: 'New Fund',
        isOptionEqualToValue: (option, value) =>
          option?.cusip === value?.trim() || option?.symbol === value?.trim(),
        options: options,
        renderOption: (props, fund: ProgramFund) => {
          const { key, ...optionProps } = props;
          return (
            <Box component='li' key={key} p={1} {...optionProps}>
              <DataTableStackCell
                primary={`${fund.symbol} | ${fund.cusip}`}
                secondary={fund.fundName}
              />
            </Box>
          );
        },
        required: true,
        suppressMenu: true,
        type: 'autocomplete',
        valueParser: value => {
          if (value?.cusip) return value.cusip?.trim();
          else return value?.trim();
        },
        width: 250
      }
    ],
    [conversion?.status, options]
  );

  const { onClicked: editFileClicked, isLoading: isLoadingEditFile } =
    useConversionFile(
      onConversionUpload,
      sponsorPlanId,
      CONVERSION_TYPES.CUSIP_MAPPING,
      conversion?.documentId,
      columnDefs
    );

  const conversionFileExists = async (fileName: string): Promise<boolean> => {
    userFileNameRef.current = fileName;
    return await PlanService.conversionFileExists(sponsorPlanId, fileName);
  };

  const uniqFunds = useMemo(
    () => [
      ...new Set(
        conversionAgGrid.gridRows.flatMap(row =>
          row.ssn && row.cusip ? [row.cusip] : []
        )
      )
    ],
    [conversionAgGrid.gridRows]
  ) as unknown as string[];

  const rowsToDto = () => {
    return conversionAgGrid.gridRows
      .filter((row: any) => !row?.pristine)
      .map((row: any) => {
        return {
          newFund: row?.newFund,
          oldFund: row?.oldFund
        };
      });
  };

  return (
    <>
      {conversion ? (
        <ConversionCardButton
          conversion={conversion}
          isLoading={isLoadingEditFile}
          onClick={() => {
            togglePlanActionDialog(true);
            editFileClicked();
          }}
          sponsorPlanId={sponsorPlanId}
        />
      ) : (
        !isHasActiveConversion && (
          <LoadingButton
            disabled={!sponsorPlanId || isLoading}
            loading={isLoading}
            onClick={() => {
              togglePlanActionDialog(true);
            }}
            startIcon={<FileUploadOutlinedIcon />}>
            Upload
          </LoadingButton>
        )
      )}
      <Dialog
        fullWidth
        maxWidth='lg'
        onClose={() => {
          togglePlanActionDialog(false);
        }}
        open={planActionDialogOpened}>
        <DialogTitle>
          <ConversionDialogHeader
            checkFileNameExists={conversionFileExists}
            columnDefs={columnDefs}
            conversion={conversion}
            onUpload={conversionAgGrid.handleLoad}
            title='Cusip Mapping'
            viewMode={isViewMode}
          />
        </DialogTitle>
        <DialogContent>
          <ActionTableForm
            emptyRowsCount={!conversion ? 10 : undefined}
            initialValues={conversionAgGrid.gridRows}
            onChange={conversionAgGrid.setGridRows}
            onErrors={conversionAgGrid.handleErrors}
            onSubmit={conversionAgGrid.handleSubmit}
            ref={conversionAgGrid.form}
            validationSchema={validationSchema(
              programFundsQuery?.data?.flatMap(i => [i?.cusip, i?.symbol]) ?? []
            )}>
            <FileUploadTable
              columnDefs={columnDefs}
              errors={conversionAgGrid.gridErrors}
              isViewMode={isViewMode}
              onCellChanged={handleCellChange}
              onRowsChanged={conversionAgGrid.handleRowsChange}
              rowData={conversionAgGrid.gridRows}
            />
          </ActionTableForm>
        </DialogContent>
        <DialogActions>
          {([ConversionStatus.PendingApproval, ConversionStatus.Draft].includes(
            conversion?.status
          ) ||
            !conversion?.status) && (
            <>
              {userService.hasPermission(
                FeatureLevelPermissions.WRITE_CONVERSION_ASSET_MAPPING_SUBMIT
              ) && (
                <LoadingButton
                  disabled={
                    conversionAgGrid.rowCount === 0 ||
                    isLoadingEditFile ||
                    (isHasActiveConversion && isNewConversion)
                  }
                  loading={isLoading}
                  onClick={async () => {
                    try {
                      await uploadMappning({
                        conversionDto: {
                          ...conversion,
                          documentId:
                            conversion?.status &&
                            conversion?.status !== ConversionStatus.Draft
                              ? conversion.documentId
                              : undefined,
                          status: ConversionStatus.Draft
                        },
                        results: rowsToDto()
                      });
                    } finally {
                      togglePlanActionDialog(false);
                      conversionAgGrid.form.current?.setValues([]);
                    }
                  }}
                  variant='text'>
                  {isDraftStage ? 'Save as Draft' : 'Revert to Draft'}
                </LoadingButton>
              )}
              {(((isUploadingUser || isNewConversion) &&
                isDraftStage &&
                userService.hasPermission(
                  FeatureLevelPermissions.WRITE_CONVERSION_ASSET_MAPPING_SUBMIT
                )) ||
                (!isUploadingUser &&
                  userService.hasPermission(
                    FeatureLevelPermissions.WRITE_CONVERSION_ASSET_MAPPING_APPROVE
                  ))) && (
                <LoadingButton
                  disabled={
                    conversionAgGrid.gridErrors.some(
                      e =>
                        !!e?.newFund ||
                        (e?.oldFund && e.oldFund !== notFoundSecurityError)
                    ) ||
                    conversionAgGrid.rowCount === 0 ||
                    isLoadingEditFile ||
                    (isHasActiveConversion && isNewConversion) ||
                    uniqFunds.some(fund => !fundMappings[fund])
                  }
                  loading={isLoading}
                  onClick={async () => {
                    await conversionAgGrid.form?.current?.validateForm();

                    try {
                      await uploadMappning({
                        conversionDto: {
                          ...conversion,
                          documentId:
                            conversion?.status &&
                            conversion?.status !== ConversionStatus.Draft
                              ? conversion.documentId
                              : undefined,
                          status:
                            conversion?.status === ConversionStatus.Draft ||
                            !conversion?.status
                              ? ConversionStatus.PendingApproval
                              : ConversionStatus.Complete
                        },
                        results: rowsToDto()
                      });
                    } finally {
                      togglePlanActionDialog(false);
                      conversionAgGrid.form.current?.setValues([]);
                    }
                  }}
                  variant='contained'>
                  {conversion?.status === ConversionStatus.Draft ||
                  !conversion?.status
                    ? 'Submit for approval'
                    : 'Approve & Submit'}
                </LoadingButton>
              )}
            </>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};
